import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Card, ErrorNote, SimulatedNote, Spinner, Stat } from "../components/ui";
import { credits, dateTime, usd } from "../lib/format";

const PACKS = [1000, 5000, 20000];

export default function Billing() {
  const qc = useQueryClient();
  const [bought, setBought] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({ queryKey: ["billing"], queryFn: api.billing.get });
  const { data: ledger } = useQuery({
    queryKey: ["ledger"],
    queryFn: () => api.billing.ledger({ limit: 50 }),
  });

  const buy = useMutation({
    mutationFn: (n: number) => api.billing.checkout(n),
    onSuccess: (res) => {
      setBought(res.message ?? res.note ?? null);
      void qc.invalidateQueries({ queryKey: ["billing"] });
      void qc.invalidateQueries({ queryKey: ["ledger"] });
    },
  });

  if (isLoading) return <Spinner />;
  if (error) return <ErrorNote error={error} />;
  if (!data) return null;

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-xl font-semibold tracking-tight">Billing</h1>
        <p className="mt-0.5 text-sm text-muted">
          One credit is one cent. A build costs {data.build_cost}, a deploy {data.deploy_cost}. Failed builds
          and failed deploys are refunded automatically.
        </p>
      </header>

      {data.simulated && (
        <div className="mb-5">
          <SimulatedNote>
            No payment processor is configured on this deployment. Purchases below grant credits without
            charging anything — the ledger still moves, so the whole surface is exercisable, but no money
            changes hands.
          </SimulatedNote>
        </div>
      )}

      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        <Stat label="Balance" value={credits(data.balance)} sub={usd(data.balance, data.credit_cents)} />
        <Stat label="Spent (30d)" value={credits(data.spent_30d)} />
        <Stat label="Builds (30d)" value={data.builds_30d} />
        <Stat label="Plan" value={<span className="capitalize">{data.plan}</span>} />
      </div>

      {buy.error && (
        <div className="mb-4">
          <ErrorNote error={buy.error} />
        </div>
      )}
      {bought && (
        <div className="mb-4">
          <SimulatedNote>{bought}</SimulatedNote>
        </div>
      )}

      <Card className="mb-6 p-5">
        <h2 className="mb-3 text-sm font-medium">Top up</h2>
        <div className="flex flex-wrap gap-3">
          {PACKS.map((n) => (
            <button
              key={n}
              onClick={() => buy.mutate(n)}
              disabled={buy.isPending}
              className="rounded-card border border-hairline px-5 py-4 text-left transition hover:border-clay disabled:opacity-40"
            >
              <span className="block text-lg font-semibold tabular-nums">{credits(n)}</span>
              <span className="block text-xs text-muted">credits · {usd(n, data.credit_cents)}</span>
              <span className="mt-1 block text-2xs text-muted">
                ≈ {Math.floor(n / data.build_cost)} builds
              </span>
            </button>
          ))}
        </div>
      </Card>

      <Card className="overflow-hidden">
        <h2 className="border-b border-hairline px-5 py-3 text-sm font-medium">Ledger</h2>
        {!ledger || ledger.items.length === 0 ? (
          <p className="px-5 py-8 text-center text-sm text-muted">No movements yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-hairline text-left text-xs text-muted">
                <tr>
                  <th className="px-5 py-2 font-medium">When</th>
                  <th className="px-5 py-2 font-medium">Reason</th>
                  <th className="px-5 py-2 font-medium">Kind</th>
                  <th className="px-5 py-2 text-right font-medium">Change</th>
                  <th className="px-5 py-2 text-right font-medium">Balance</th>
                </tr>
              </thead>
              <tbody>
                {ledger.items.map((e) => (
                  <tr key={e.id} className="border-b border-hairline/60 last:border-0">
                    <td className="whitespace-nowrap px-5 py-2 text-xs text-muted">{dateTime(e.created_at)}</td>
                    <td className="px-5 py-2">{e.reason}</td>
                    <td className="px-5 py-2 text-xs text-muted">{e.kind.replace(/_/g, " ")}</td>
                    <td
                      className={`whitespace-nowrap px-5 py-2 text-right tabular-nums ${
                        e.delta > 0 ? "text-moss" : ""
                      }`}
                    >
                      {e.delta > 0 ? "+" : ""}
                      {credits(e.delta)}
                    </td>
                    <td className="whitespace-nowrap px-5 py-2 text-right tabular-nums text-muted">
                      {credits(e.balance_after)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
